import { ArrowRight, ArrowUpRight, Building2, FlaskConical, Handshake, Rocket } from "lucide-react"
import { Link } from "react-router"
import { PageHero } from "@/components/PageHero"
import { Reveal } from "@/components/Reveal"
import { SectionHeading } from "@/components/SectionHeading"
import { RemoteImage } from "@/components/RemoteImage"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ecosystemSupport, partners, type PartnerItem } from "@/data/site"

function PartnerGrid({ items }: { items: PartnerItem[] }) {
  return (
    <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {items.map((partner, i) => (
        <Reveal key={partner.name} delay={i * .05}>
          <a href={partner.href} target="_blank" rel="noreferrer">
            <Card className="group flex h-full items-start justify-between gap-5 p-7 transition hover:-translate-y-1 hover:border-emerald-300 dark:hover:border-emerald-400/50">
              <div>
                {partner.logo && <div className="mb-5 flex h-14 items-center"><RemoteImage src={partner.logo} alt={partner.name} className="max-h-14 w-auto object-contain" /></div>}
                <Badge className="text-emerald-800 dark:text-emerald-300">{partner.role}</Badge>
                <h3 className="mt-5 text-2xl font-semibold">{partner.name}</h3>
                <p className="mt-3 leading-7 text-slate-600 dark:text-slate-400">{partner.description}</p>
              </div>
              <ArrowUpRight className="size-5 shrink-0 text-slate-400 transition group-hover:text-emerald-700 dark:group-hover:text-emerald-400" />
            </Card>
          </a>
        </Reveal>
      ))}
    </div>
  )
}

function LogoMarquee({ items }: { items: PartnerItem[] }) {
  const display = [...items, ...items]
  return (
    <div className="mt-12 overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[.04]">
      <div className="marquee-track flex min-w-max gap-4">
        {display.map((item, index) => (
          <div key={`${item.name}-${index}`} className="flex h-24 w-60 shrink-0 items-center justify-center rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/[.06]">
            {item.logo ? <RemoteImage src={item.logo} alt={item.name} className="max-h-14 w-auto object-contain" /> : <div className="text-sm font-semibold text-slate-600 dark:text-slate-300">{item.name}</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function Partners() {
  const allLogoItems = [...partners, ...ecosystemSupport].filter((item) => item.logo)

  return (
    <>
      <PageHero eyebrow="Partners & Ecosystem" title="Global climate infrastructure is built through collaboration." description="Smart Drain works across engineering, market access, research, commercialisation, acceleration and investment readiness to move from field evidence to scalable deployment." />

      <section className="px-6 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal><SectionHeading eyebrow="Our partners" title="A running showcase of the organisations supporting Smart Drain." description="Partner logos and brand visuals from the supplied materials are now integrated into the website." /></Reveal>
          <Reveal delay={.05}><LogoMarquee items={allLogoItems} /></Reveal>
        </div>
      </section>

      <section className="px-6 pb-24 sm:pb-32">
        <div className="mx-auto max-w-7xl"><Reveal><SectionHeading eyebrow="Deployment & market partners" title="Partners helping turn the technology into operating infrastructure." /></Reveal><PartnerGrid items={partners} /></div>
      </section>

      <section className="bg-slate-100 px-6 py-24 sm:py-32 dark:bg-white/[.03]"><div className="mx-auto max-w-7xl"><Reveal><SectionHeading eyebrow="Accelerators, investment & ecosystem support" title="Commercialisation support for the next stage of scale." description="Program work strengthens product development, investment readiness, startup growth and market pathways." /></Reveal><PartnerGrid items={ecosystemSupport} /></div></section>

      <section className="bg-[#061a1d] px-6 py-24 text-white sm:py-32"><div className="mx-auto max-w-7xl"><Reveal><SectionHeading light eyebrow="Collaborate with Smart Drain" title="We are actively looking for the partners required to deploy globally." /></Reveal><div className="mt-12 grid gap-5 md:grid-cols-3">{[
        [Building2, "Pilot collaboration", "Cities, councils, property developments and infrastructure owners with a recurring drainage problem site."],
        [FlaskConical, "Research partnership", "Technical validation, field studies and joint grant opportunities in drainage, flood resilience, litter interception, IoT and smart-city infrastructure."],
        [Handshake, "Technical collaboration", "Manufacturing, materials and technology capability spanning aluminium, stainless steel, galvanised steel, HDPE, IoT hardware, sensors, communications and platform development."],
      ].map(([Icon, title, body], i) => {
        const I = Icon as typeof Building2
        return <Reveal key={String(title)} delay={i * .06}><div className="h-full rounded-[1.5rem] border border-white/10 bg-white/[.04] p-7"><I className="size-6 text-emerald-200" /><h3 className="mt-6 text-2xl font-semibold">{String(title)}</h3><p className="mt-3 leading-7 text-white/50">{String(body)}</p></div></Reveal>
      })}</div><div className="mt-10 flex flex-wrap gap-3"><Link to="/contact"><Button>Discuss collaboration <ArrowRight className="size-4" /></Button></Link><Link to="/start-pilot"><Button variant="secondary">Start a Pilot <Rocket className="size-4" /></Button></Link></div></div></section>
    </>
  )
}
